import React, { useMemo, useState } from "react";
import axios from "axios";
import "./signup.css";
import { PCB_CORE_URL } from "../../config";

const API_SIGNUP = `${PCB_CORE_URL}/signup/`;

const emptyShipping = {
  shipping_contact_name: "",
  shipping_service: "",
  shipping_address_line1: "",
  shipping_address_line2: "",
  shipping_postal_code: "",
  shipping_city: "",
  shipping_country: "",
};

export default function Signup() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyIdentifier, setCompanyIdentifier] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState("");
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingAddressLine2, setBillingAddressLine2] = useState("");
  const [billingPostalCode, setBillingPostalCode] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingCountry, setBillingCountry] = useState("");
  const [shippingSameAsBilling, setShippingSameAsBilling] = useState(true);
  const [shipping, setShipping] = useState(emptyShipping);
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const pwdMatch = password1 === password2;

  const billingReady = useMemo(() => {
    return (
      billingAddressLine1.trim().length >= 3 &&
      billingPostalCode.trim().length >= 2 &&
      billingCity.trim().length >= 2 &&
      (billingCountry.trim().length >= 2 || country.trim().length >= 2)
    );
  }, [billingAddressLine1, billingPostalCode, billingCity, billingCountry, country]);

  const shippingReady = useMemo(() => {
    if (shippingSameAsBilling) return true;
    return (
      shipping.shipping_address_line1.trim().length >= 3 &&
      shipping.shipping_postal_code.trim().length >= 2 &&
      shipping.shipping_city.trim().length >= 2 &&
      shipping.shipping_country.trim().length >= 2
    );
  }, [shippingSameAsBilling, shipping]);

  const canSubmit = useMemo(() => {
    return (
      firstName.trim().length >= 2 &&
      lastName.trim().length >= 2 &&
      email.trim().length > 3 &&
      companyName.trim().length >= 2 &&
      phoneNumber.trim().length >= 6 &&
      country.trim().length >= 2 &&
      billingReady &&
      shippingReady &&
      password1.trim().length >= 6 &&
      pwdMatch &&
      acceptPrivacy &&
      acceptTerms &&
      !loading
    );
  }, [
    firstName,
    lastName,
    email,
    companyName,
    phoneNumber,
    country,
    billingReady,
    shippingReady,
    password1,
    pwdMatch,
    acceptPrivacy,
    acceptTerms,
    loading,
  ]);

  function updateShipping(field, value) {
    setShipping((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError("");
    setSuccessMsg("");

    if (!canSubmit) return;

    setLoading(true);
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        company_name: companyName.trim(),
        company_identifier: companyIdentifier.trim(),
        phone_number: phoneNumber.trim(),
        country: country.trim(),
        billing_address_line1: billingAddressLine1.trim(),
        billing_address_line2: billingAddressLine2.trim(),
        billing_postal_code: billingPostalCode.trim(),
        billing_city: billingCity.trim(),
        billing_country: (billingCountry.trim() || country.trim()),
        shipping_same_as_billing: shippingSameAsBilling,
        shipping_contact_name: shippingSameAsBilling ? "" : shipping.shipping_contact_name.trim(),
        shipping_service: shippingSameAsBilling ? "" : shipping.shipping_service.trim(),
        shipping_address_line1: shippingSameAsBilling ? "" : shipping.shipping_address_line1.trim(),
        shipping_address_line2: shippingSameAsBilling ? "" : shipping.shipping_address_line2.trim(),
        shipping_postal_code: shippingSameAsBilling ? "" : shipping.shipping_postal_code.trim(),
        shipping_city: shippingSameAsBilling ? "" : shipping.shipping_city.trim(),
        shipping_country: shippingSameAsBilling ? (billingCountry.trim() || country.trim()) : shipping.shipping_country.trim(),
        password: password1,
        accepted_privacy_policy: acceptPrivacy,
        accepted_terms: acceptTerms,
      };

      await axios.post(API_SIGNUP, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setFirstName("");
      setLastName("");
      setEmail("");
      setCompanyName("");
      setCompanyIdentifier("");
      setPhoneNumber("");
      setCountry("");
      setBillingAddressLine1("");
      setBillingAddressLine2("");
      setBillingPostalCode("");
      setBillingCity("");
      setBillingCountry("");
      setShippingSameAsBilling(true);
      setShipping(emptyShipping);
      setPassword1("");
      setPassword2("");
      setAcceptPrivacy(false);
      setAcceptTerms(false);
      setSuccessMsg("Notre application est toujours en cours de développement. Elle sera bientôt terminée. Merci pour votre intérêt, nous vous recontacterons dès qu'elle sera opérationnelle.");
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        data?.detail ||
        data?.Message ||
        data?.message ||
        (data?.email && Array.isArray(data.email) ? data.email[0] : null) ||
        (data?.password && Array.isArray(data.password) ? data.password[0] : null) ||
        (data?.first_name && Array.isArray(data.first_name) ? data.first_name[0] : null) ||
        (data?.last_name && Array.isArray(data.last_name) ? data.last_name[0] : null) ||
        err?.message ||
        "Impossible de créer le compte.";

      setServerError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        <div className="auth-header">
          <h1 className="auth-title">Créer un compte</h1>
          <p className="auth-subtitle">Renseignez vos informations société, facturation et livraison.</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-section-title">Contact</div>
          <div className="auth-grid two-cols">
            <label className="auth-label">
              Prénom
              <input className="auth-input" type="text" autoComplete="given-name" placeholder="ex: Sami" value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={loading} required />
            </label>
            <label className="auth-label">
              Nom
              <input className="auth-input" type="text" autoComplete="family-name" placeholder="ex: Martin" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={loading} required />
            </label>
            <label className="auth-label">
              Email
              <input className="auth-input" type="email" autoComplete="email" placeholder="ex: martin@email.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} required />
            </label>
            <label className="auth-label">
              Téléphone
              <input className="auth-input" type="tel" placeholder="ex: +33..." value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} disabled={loading} required />
            </label>
          </div>

          <div className="auth-section-title">Société</div>
          <div className="auth-grid two-cols">
            <label className="auth-label">
              Société
              <input className="auth-input" type="text" placeholder="ex: Eviacore" value={companyName} onChange={(e) => setCompanyName(e.target.value)} disabled={loading} required />
            </label>
            <label className="auth-label">
              SIRET / identifiant société
              <input className="auth-input" type="text" placeholder="ex: 12345678900011" value={companyIdentifier} onChange={(e) => setCompanyIdentifier(e.target.value)} disabled={loading} />
            </label>
            <label className="auth-label auth-span-2">
              Pays
              <input className="auth-input" type="text" placeholder="ex: France" value={country} onChange={(e) => setCountry(e.target.value)} disabled={loading} required />
            </label>
          </div>

          <div className="auth-section-title">Adresse de facturation</div>
          <div className="auth-grid two-cols">
            <label className="auth-label auth-span-2">
              Adresse ligne 1
              <input className="auth-input" type="text" placeholder="Numéro et rue" value={billingAddressLine1} onChange={(e) => setBillingAddressLine1(e.target.value)} disabled={loading} required />
            </label>
            <label className="auth-label auth-span-2">
              Adresse ligne 2
              <input className="auth-input" type="text" placeholder="Bâtiment, étage, complément" value={billingAddressLine2} onChange={(e) => setBillingAddressLine2(e.target.value)} disabled={loading} />
            </label>
            <label className="auth-label">
              Code postal
              <input className="auth-input" type="text" placeholder="ex: 75001" value={billingPostalCode} onChange={(e) => setBillingPostalCode(e.target.value)} disabled={loading} required />
            </label>
            <label className="auth-label">
              Ville
              <input className="auth-input" type="text" placeholder="ex: Paris" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} disabled={loading} required />
            </label>
            <label className="auth-label auth-span-2">
              Pays de facturation
              <input className="auth-input" type="text" placeholder="ex: France" value={billingCountry} onChange={(e) => setBillingCountry(e.target.value)} disabled={loading} />
            </label>
          </div>
           <label className="auth-check auth-check-inline">
            <input type="checkbox" checked={shippingSameAsBilling} onChange={(e) => setShippingSameAsBilling(e.target.checked)} disabled={loading} />
            <span>Souhaitez-vous conserver cette adresse pour l'adresse de livraison ?</span>
          </label>
          <div className="auth-section-title">Adresse de livraison</div>
         

          {!shippingSameAsBilling ? (
            <div className="auth-grid two-cols">
              <label className="auth-label">
                Nom du contact
                <input className="auth-input" type="text" placeholder="Personne ou équipe" value={shipping.shipping_contact_name} onChange={(e) => updateShipping("shipping_contact_name", e.target.value)} disabled={loading} />
              </label>
              <label className="auth-label">
                Service (facultatif)
                <input className="auth-input" type="text" placeholder="ex: réception, atelier" value={shipping.shipping_service} onChange={(e) => updateShipping("shipping_service", e.target.value)} disabled={loading} />
              </label>
              <label className="auth-label auth-span-2">
                Adresse ligne 1
                <input className="auth-input" type="text" placeholder="Numéro et rue" value={shipping.shipping_address_line1} onChange={(e) => updateShipping("shipping_address_line1", e.target.value)} disabled={loading} required={!shippingSameAsBilling} />
              </label>
              <label className="auth-label auth-span-2">
                Adresse ligne 2
                <input className="auth-input" type="text" placeholder="Bâtiment, étage, complément" value={shipping.shipping_address_line2} onChange={(e) => updateShipping("shipping_address_line2", e.target.value)} disabled={loading} />
              </label>
              <label className="auth-label">
                Code postal
                <input className="auth-input" type="text" placeholder="ex: 69002" value={shipping.shipping_postal_code} onChange={(e) => updateShipping("shipping_postal_code", e.target.value)} disabled={loading} required={!shippingSameAsBilling} />
              </label>
              <label className="auth-label">
                Ville
                <input className="auth-input" type="text" placeholder="ex: Lyon" value={shipping.shipping_city} onChange={(e) => updateShipping("shipping_city", e.target.value)} disabled={loading} required={!shippingSameAsBilling} />
              </label>
              <label className="auth-label auth-span-2">
                Pays de livraison
                <input className="auth-input" type="text" placeholder="ex: France" value={shipping.shipping_country} onChange={(e) => updateShipping("shipping_country", e.target.value)} disabled={loading} required={!shippingSameAsBilling} />
              </label>
            </div>
          ) : null}

          <div className="auth-section-title">Sécurité</div>
          <label className="auth-label">
            Mot de passe
            <input className="auth-input" type={showPwd ? "text" : "password"} autoComplete="new-password" placeholder="Minimum 6 caracteres" value={password1} onChange={(e) => setPassword1(e.target.value)} disabled={loading} required />
          </label>

          <label className="auth-label">
            Confirmer le mot de passe
            <div className="pwd-row">
              <input className="auth-input" type={showPwd ? "text" : "password"} autoComplete="new-password" placeholder="Retapez le mot de passe" value={password2} onChange={(e) => setPassword2(e.target.value)} disabled={loading} required />
              <button type="button" className="pwd-toggle" onClick={() => setShowPwd((v) => !v)} disabled={loading}>
                {showPwd ? "Masquer" : "Voir"}
              </button>
            </div>
          </label>

          <label className="auth-check">
            <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} disabled={loading} required />
            <span>
              J'accepte que mes données personnelles soient traitées conformément à la page <a className="auth-link" href="https://eviacore.com/fr/privacy-policy" target="_blank" rel="noreferrer">Données personnelles</a>.
            </span>
          </label>

          <label className="auth-check">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} disabled={loading} required />
            <span>
              J'accepte les <a className="auth-link" href="https://eviacore.com/fr/cgv" target="_blank" rel="noreferrer">Conditions Générales de Vente</a> et la page <a className="auth-link" href="https://eviacore.com/fr/privacy-policy" target="_blank" rel="noreferrer">Données personnelles</a>.
            </span>
          </label>

          {!pwdMatch && password2.length > 0 ? <div className="auth-alert warn">Les mots de passe ne correspondent pas.</div> : null}
          {serverError ? <div className="auth-alert error">{serverError}</div> : null}
          {successMsg ? <div className="auth-alert success">{successMsg}</div> : null}

          <button className="auth-btn" type="submit" disabled={!canSubmit}>
            {loading ? "Création..." : "Créer mon compte"}
          </button>

          <div className="auth-footer">
            <span>Déjà un compte ?</span>
            <a className="auth-link" href="/login">Se connecter</a>
          </div>
        </form>
      </div>
    </div>
  );
}
